/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { OPEN_QUOTE_TEMPLATE_MUTATION } from './graphql/openQuoteTemplate';
import { transformQuoteTemplate } from '@/quote-management/data/transforms';
import { state } from '@/quote-management/lib/state';
import { events } from '@adobe-commerce/event-bus';

export interface OpenQuoteTemplateParams {
  templateId: string;
}

export const openQuoteTemplate = async (params: OpenQuoteTemplateParams) => {
  if (!params.templateId) {
    throw new Error('Template ID is required');
  }

  if (!state.authenticated) {
    throw new Error('Unauthorized');
  }

  try {
    const results: any = await fetchGraphQl(OPEN_QUOTE_TEMPLATE_MUTATION, {
      variables: { templateId: params.templateId },
    });

    if (!results?.data?.openNegotiableQuoteTemplate) {
      throw new Error('No quote template data received');
    }

    const transformedData = transformQuoteTemplate(
      results.data.openNegotiableQuoteTemplate
    );

    if (!transformedData) {
      throw new Error('Failed to transform quote template data');
    }

    events.emit('quote-management/quote-template-data', {
      quoteTemplate: transformedData,
      permissions: state.permissions,
    });

    return transformedData;
  } catch (error) {
    return Promise.reject(error);
  }
};
